import * as React from "react";
import request from "src/utils/request";
import { useParams } from "react-router-dom";
import { useActiveIntegrationsTeams } from "src/scenes/teams/dashboard/active-integrations-provider-teams";
import { useAuth } from "src/components/providers/auth-context";

export type Sprint = { id: string; name: string; startDate: string; endDate: string };

const TeamSprintsContext = React.createContext<{
    allProjects?: { projects: [] };
    allSprints?: Sprint[];
    selectedSprint?: Sprint;
    selectedSprints?: Sprint[];
    handleSprintChange?: (sprints: Sprint[]) => void;
    selectedProject?: string;
    handleProjectChange?: (project: string) => void;
    isScrum?: boolean;
    sprintMode?: "sprints" | "dates";
    setSprintMode?: (mode: "sprints" | "dates") => void;
    hasTicketIntegration?: boolean;
}>({});
TeamSprintsContext.displayName = "SprintsContextTeams";

const TeamSprintsContextProvider = (props) => {
    const { teamId } = useParams();
    const { isDemo, userHasTeamsSubscription } = useAuth();
    const { activeIntegrations: activeIntegrationsTeams } = useActiveIntegrationsTeams();
    const [selectedSprints, setSelectedSprints] = React.useState<Sprint[]>([]);
    const [allSprints, setAllSprints] = React.useState([]);
    const [allProjects, setAllProjects] = React.useState<{ projects: { projectKey: string }[] }>({
        projects: [],
    });
    const [isScrum, setIsScrum] = React.useState(false);
    const [sprintMode, setSprintMode] = React.useState<"sprints" | "dates">("sprints");
    const [selectedProject, setSelectedProject] = React.useState<string>(
        () => localStorage.getItem(`${teamId}:project`) || "All"
    );
    const isTeamJiraIntegrated = !!activeIntegrationsTeams?.data?.integrations?.find(
        (int) => int.name === "jira"
    )?.connected;
    const isTeamNotionIntegrated = !!activeIntegrationsTeams?.data?.integrations?.find(
        (int) => int.name === "notion"
    )?.connected;

    const getSprints = async (projectKey?: string | null) => {
        let response;
        let projectRes;
        const effectiveProject = projectKey ?? null;
        const projectParam =
            effectiveProject && effectiveProject !== "All"
                ? `?project=${encodeURIComponent(effectiveProject)}`
                : "";
        if (userHasTeamsSubscription && (isTeamJiraIntegrated || isTeamNotionIntegrated)) {
            [response, projectRes] = await Promise.all([
                request(`/api/data/fundamentals/teams/${teamId}/v1/jira-sprints${projectParam}`, {
                    method: "GET",
                }),
                request(`/api/data/fundamentals/teams/${teamId}/v1/jira-projects`, {
                    method: "GET",
                }),
            ]);
        }
        if (
            isDemo ||
            (!isTeamJiraIntegrated && !isTeamNotionIntegrated) ||
            !userHasTeamsSubscription
        ) {
            // @ts-expect-error: Ignore TS1323 for dynamic import
            response = (await import("../../dashboard/mock-data/work-fundamentals/jira-sprints"))
                .default;
            // @ts-expect-error: Ignore TS1323 for dynamic import
            projectRes = (await import("../../dashboard/mock-data/work-fundamentals/jira-projects"))
                .default;
        }
        const filteredSprints = response.filter((sprint) => sprint.startDate && sprint.endDate);
        const hasDefaultProject = (projectRes?.projects ?? []).some(
            (p: { default?: boolean }) => p.default === true
        );
        setAllSprints(filteredSprints);
        setIsScrum(filteredSprints.length > 0);
        if (filteredSprints.length > 0) {
            const defaultSprint =
                filteredSprints.find((s) => s.state === "closed") ?? filteredSprints[0];
            setSelectedSprints([defaultSprint]);
            setSprintMode(hasDefaultProject ? "sprints" : "dates");
        } else {
            setSprintMode("dates");
        }
        if (projectRes && projectRes.projects && projectRes.projects.length > 0) {
            setAllProjects({ projects: [...projectRes.projects] });
        } else {
            setAllProjects({ projects: [] });
        }
    };

    React.useEffect(() => {
        if (activeIntegrationsTeams.status === "SUCCESS") getSprints(selectedProject);
    }, [activeIntegrationsTeams.status]);

    const handleSprintChange = React.useCallback((newSelectedSprints: Sprint[]) => {
        setSelectedSprints(newSelectedSprints);
    }, []);

    const handleProjectChange = React.useCallback(
        (project: string) => {
            setSelectedProject(project);
            localStorage.setItem(`${teamId}:project`, project);
            getSprints(project);
        },
        [teamId, isTeamJiraIntegrated, isTeamNotionIntegrated, userHasTeamsSubscription, isDemo]
    );

    const value = React.useMemo(
        () => ({
            handleSprintChange,
            selectedSprint: selectedSprints[0],
            selectedSprints,
            allSprints,
            allProjects,
            isScrum,
            sprintMode,
            setSprintMode,
            selectedProject,
            handleProjectChange,
            hasTicketIntegration: isTeamJiraIntegrated || isTeamNotionIntegrated,
        }),
        [
            handleSprintChange,
            selectedSprints,
            allSprints,
            allProjects,
            isScrum,
            sprintMode,
            selectedProject,
            handleProjectChange,
            isTeamJiraIntegrated,
            isTeamNotionIntegrated,
        ]
    );

    return <TeamSprintsContext.Provider value={value} {...props} />;
};

const useTeamSprints = () => {
    const context = React.useContext(TeamSprintsContext);
    if (context === undefined) {
        throw new Error(`useSprints must be used within a TeamSprintsProvider`);
    }
    return context;
};

export { TeamSprintsContext, TeamSprintsContextProvider, useTeamSprints };
